import React, { useState } from "react";
import Layout from "../components/Layout";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  House, Wrench, DotsThree, PaperPlaneTilt, Clock, User as UserIcon,
  Envelope, Lock, Heart, Handshake, ShieldCheck, HardHat,
  CheckCircle, CaretRight,
} from "@phosphor-icons/react";

const ROLES = [
  { id: "homeowner", label: "Homeowner", icon: House },
  { id: "tradesperson", label: "Tradesperson", icon: HardHat },
  { id: "other", label: "Other", icon: DotsThree },
];

const SUBJECTS = [
  { id: "", label: "Please select a subject", disabled: true },
  { id: "general", label: "General enquiry" },
  { id: "homeowner-help", label: "Homeowner support" },
  { id: "tradesperson-help", label: "Tradesperson support" },
  { id: "billing", label: "Billing & subscription" },
  { id: "partnership", label: "Partnership / business" },
  { id: "press-media", label: "Press & media" },
  { id: "report-issue", label: "Report an issue / safety concern" },
  { id: "other", label: "Something else" },
];

export default function Contact() {
  const [role, setRole] = useState("homeowner");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  React.useEffect(() => { document.title = "Contact Us · MyHandyHub"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Please tick the consent box so we can reply.");
      return;
    }
    if (!subject) {
      toast.error("Please pick a subject so we can route your message.");
      return;
    }
    if (message.trim().length < 10) {
      toast.error("Please write at least a sentence so we can help properly.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", {
        role, name: name.trim(), email: email.trim(),
        phone: phone.trim() || undefined,
        subject, message: message.trim(), consent, website,
      });
      setSent(true);
      toast.success("Message sent — we'll be in touch within 24 hours.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-5 lg:px-8 py-20 text-center" data-testid="contact-success">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full" style={{ background: "rgba(17,82,59,0.10)" }}>
            <CheckCircle size={48} weight="duotone" style={{ color: "var(--brand-green)" }} />
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl mt-6" style={{ color: "var(--brand-green)" }}>
            Message <span style={{ color: "var(--brand-orange)" }}>received!</span>
          </h1>
          <p className="text-base text-[var(--text-muted)] mt-3 max-w-xl mx-auto">
            Thanks {name.split(" ")[0]}. A member of the MyHandyHub team will reply to <strong>{email}</strong> within 24 hours.
            Keep an eye on your inbox (and your spam folder, just in case).
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/" className="btn-primary" data-testid="contact-success-home">Back to home</Link>
            <Link to="/faq" className="btn-ghost" data-testid="contact-success-faq">Browse the FAQ</Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-12 sm:py-16" data-testid="contact-page">
        {/* Hero */}
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider" style={{ background: "rgba(226,109,41,0.12)", color: "var(--brand-orange)" }} data-testid="contact-eyebrow">
              We&apos;re here to help
            </div>
            <h1 className="font-display font-extrabold text-5xl sm:text-6xl lg:text-7xl mt-5 leading-[1.02]" style={{ color: "var(--brand-green)" }}>
              Need Help?
              <br />
              <span style={{ color: "var(--brand-orange)" }}>Get in Touch.</span>
            </h1>
            <p className="text-base sm:text-lg text-[var(--text-muted)] mt-5 max-w-md">
              Whether you&apos;re a homeowner, tradesperson or partner, we&apos;re here to help.
            </p>
            <div className="mt-6 inline-flex items-center gap-2.5 px-4 py-2.5 rounded-full" style={{ background: "rgba(17,82,59,0.08)" }} data-testid="contact-reply-badge">
              <CheckCircle size={20} weight="duotone" style={{ color: "var(--brand-green)" }} />
              <span className="font-semibold text-sm" style={{ color: "var(--brand-green)" }}>
                We reply within <span style={{ color: "var(--brand-orange)" }}>24 hours</span>
              </span>
            </div>
          </div>
          <HeroIllustration />
        </div>

        {/* Form + Sidebar */}
        <div className="grid lg:grid-cols-3 gap-6 mt-10 sm:mt-14">
          {/* Form */}
          <form onSubmit={submit} className="lg:col-span-2 brand-card p-6 sm:p-8" data-testid="contact-form">
            <h2 className="font-display font-extrabold text-2xl" style={{ color: "var(--brand-green)" }}>Send us a message</h2>
            <p className="text-sm text-[var(--text-muted)] mt-1">
              Fill in the form below and a member of our team will get back to you within{" "}
              <strong style={{ color: "var(--brand-orange)" }}>24 hours</strong>.
            </p>

            {/* Role */}
            <div className="mt-6">
              <label className="block text-sm font-bold" style={{ color: "var(--brand-green)" }}>I am a…</label>
              <div className="grid grid-cols-3 gap-2 mt-2" role="radiogroup">
                {ROLES.map((r) => {
                  const Icon = r.icon;
                  const active = role === r.id;
                  return (
                    <button
                      type="button"
                      key={r.id}
                      onClick={() => setRole(r.id)}
                      data-testid={`contact-role-${r.id}`}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                        active
                          ? "border-[var(--brand-orange)] bg-[rgba(226,109,41,0.06)]"
                          : "border-[var(--border-stone)] hover:border-[var(--brand-green)] hover:bg-[#FAF8F2]"
                      }`}
                    >
                      <Icon size={24} weight={active ? "fill" : "duotone"} style={{ color: active ? "var(--brand-orange)" : "var(--brand-green)" }} />
                      <span className="text-sm font-semibold" style={{ color: "var(--brand-green)" }}>{r.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Honeypot - hidden from humans */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }}>
              <label>Website (leave blank)
                <input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
              </label>
            </div>

            {/* Name */}
            <div className="mt-5">
              <label className="block text-sm font-bold mb-1.5" style={{ color: "var(--brand-green)" }}>
                Full Name <span style={{ color: "var(--brand-orange)" }}>*</span>
              </label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your full name"
                className="brand-input w-full"
                data-testid="contact-name"
              />
            </div>

            {/* Email + Phone */}
            <div className="grid sm:grid-cols-2 gap-4 mt-5">
              <div>
                <label className="block text-sm font-bold mb-1.5" style={{ color: "var(--brand-green)" }}>
                  Email Address <span style={{ color: "var(--brand-orange)" }}>*</span>
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="brand-input w-full"
                  data-testid="contact-email"
                />
              </div>
              <div>
                <label className="block text-sm font-bold mb-1.5" style={{ color: "var(--brand-green)" }}>
                  Phone Number <span className="text-xs font-normal text-[var(--text-muted)] ml-1">(optional)</span>
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Enter your phone number"
                  className="brand-input w-full"
                  data-testid="contact-phone"
                />
              </div>
            </div>

            {/* Subject */}
            <div className="mt-5">
              <label className="block text-sm font-bold mb-1.5" style={{ color: "var(--brand-green)" }}>
                Subject <span style={{ color: "var(--brand-orange)" }}>*</span>
              </label>
              <select
                required
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="brand-input w-full"
                data-testid="contact-subject"
              >
                {SUBJECTS.map((s) => (
                  <option key={s.id || "placeholder"} value={s.id} disabled={s.disabled}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Message */}
            <div className="mt-5">
              <label className="block text-sm font-bold mb-1.5" style={{ color: "var(--brand-green)" }}>
                Message <span style={{ color: "var(--brand-orange)" }}>*</span>
              </label>
              <textarea
                required
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us how we can help…"
                maxLength={4000}
                className="brand-input w-full font-sans"
                data-testid="contact-message"
              />
              <div className="text-xs text-[var(--text-muted)] mt-1 text-right">{message.length} / 4000</div>
            </div>

            {/* Consent */}
            <label className="mt-2 flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5"
                data-testid="contact-consent"
              />
              <span className="text-sm text-[var(--text-main)]">I agree to be contacted regarding my enquiry.</span>
            </label>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full mt-5 justify-center text-base py-3.5"
              data-testid="contact-submit"
            >
              <PaperPlaneTilt size={18} weight="fill" />
              {submitting ? "Sending…" : "Send Message"}
            </button>

            <p className="flex items-center justify-center gap-1.5 text-xs text-[var(--text-muted)] mt-3">
              <Lock size={12} weight="fill" /> We respect your privacy and will never share your details.
            </p>
          </form>

          {/* Sidebar */}
          <aside className="brand-card p-6 sm:p-7 h-fit space-y-7" style={{ background: "#FAF8F2" }} data-testid="contact-sidebar">
            <div>
              <h3 className="font-display font-extrabold text-lg" style={{ color: "var(--brand-green)" }}>What happens next?</h3>
              <ul className="mt-4 space-y-4">
                {[
                  { icon: Clock, text: <>We&apos;ll confirm we&apos;ve received your enquiry.</> },
                  { icon: UserIcon, text: <>A member of the MyHandyHub team will respond within <strong style={{ color: "var(--brand-orange)" }}>24 hours.</strong></> },
                  { icon: Envelope, text: <>We&apos;ll do our best to provide the information or support you need.</> },
                ].map((step, i) => {
                  const Icon = step.icon;
                  return (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(17,82,59,0.10)" }}>
                        <Icon size={20} weight="duotone" style={{ color: "var(--brand-green)" }} />
                      </span>
                      <span className="text-sm leading-relaxed text-[var(--text-main)] pt-2">{step.text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="border-t border-[var(--border-stone)] pt-6">
              <h3 className="font-display font-extrabold text-lg" style={{ color: "var(--brand-green)" }}>Need help sooner?</h3>
              <p className="text-sm text-[var(--text-main)] mt-2">Email us anytime:</p>
              <a
                href="mailto:support@myhandyhub.co.uk"
                className="block mt-1 text-base font-bold hover:underline"
                style={{ color: "var(--brand-orange)" }}
                data-testid="contact-sidebar-email"
              >
                support@myhandyhub.co.uk
              </a>
            </div>

            <div className="border-t border-[var(--border-stone)] pt-6">
              <h3 className="font-display font-extrabold text-lg" style={{ color: "var(--brand-green)" }}>We&apos;re here for</h3>
              <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
                <div className="flex items-center gap-2"><House size={20} weight="duotone" style={{ color: "var(--brand-green)" }} /> Homeowners</div>
                <div className="flex items-center gap-2"><HardHat size={20} weight="duotone" style={{ color: "var(--brand-green)" }} /> Tradespeople</div>
                <div className="flex items-center gap-2"><Handshake size={20} weight="duotone" style={{ color: "var(--brand-green)" }} /> Partners</div>
              </div>
            </div>
          </aside>
        </div>

        {/* Trust band */}
        <div className="mt-12 rounded-2xl p-6 sm:p-8" style={{ background: "var(--brand-green)" }} data-testid="contact-trust-band">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: ShieldCheck, title: "Trusted Platform", lines: ["Verified trades.", "Real reviews. Local jobs."], green: true },
              { icon: HardHat, title: "Built for Trades", lines: ["Fair pricing.", "No lead fees."], orange: true },
              { icon: House, title: "Helping Homeowners", lines: ["Find trusted local trades", "in minutes."], green: true },
              { icon: Heart, title: "Friendly Support", lines: ["We're here to", "help you succeed."], orange: true },
            ].map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.title} className="flex items-center gap-4 text-white">
                  <span className="flex-shrink-0 w-14 h-14 rounded-full bg-white flex items-center justify-center">
                    <Icon size={26} weight="fill" style={{ color: b.orange ? "var(--brand-orange)" : "var(--brand-green)" }} />
                  </span>
                  <div>
                    <div className="font-display font-extrabold text-base">{b.title}</div>
                    {b.lines.map((l, i) => (
                      <div key={i} className="text-xs opacity-90 leading-snug">{l}</div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* FAQ teaser */}
        <div className="mt-10 text-center">
          <Link to="/faq" className="inline-flex items-center gap-1.5 text-sm font-bold hover:underline" style={{ color: "var(--brand-green)" }} data-testid="contact-faq-link">
            Check the FAQ for instant answers <CaretRight size={14} weight="bold" />
          </Link>
        </div>
      </div>
    </Layout>
  );
}

function HeroIllustration() {
  return (
    <div className="relative max-w-md mx-auto lg:max-w-none lg:ml-auto" data-testid="contact-hero-illustration">
      <img
        src="/contact-illustration.png"
        alt="A friendly MyHandyHub support agent ready to help"
        className="w-full h-auto"
        loading="eager"
      />
    </div>
  );
}
